import {
    animationFrameScheduler,
    fromEvent,
    fromEventPattern,
    merge,
    Observable,
    of,
    scheduled,
    Subject,
    Subscription,
} from 'rxjs';
import { auditTime, catchError, takeUntil, tap } from 'rxjs/operators';

export type DetectChangesFn = () => void;
export type DestroyFn = <T extends BaseNode>(node: T) => void;

export interface RenderContext {
    element: HTMLElement;
    detectChanges: DetectChangesFn;
    destroy: DestroyFn;

    appendElement?: HTMLElement;
}

export type RenderFn = (context: Context) => RenderContext;

export interface Definition<T = any> {
    ctx?: T;
    render?: RenderFn;
}

export interface ExperimentalTabset extends Definition {}

export interface Tab<T = any> extends Definition<T> {
    id: string;
    fixed?: boolean;
}

export interface Context<T = any> {
    type: 'tabset' | 'tab';
    tab?: TabNode;
    ctx?: T;
}

export abstract class BaseNode<
    TDefinition extends Definition = any,
    TCtx = any,
> {
    def: TDefinition;
    context: Context<TCtx>;
    renderContext: RenderContext;

    abstract detectChanges(opts?: BubbleOpts): void;

    abstract destroy(): void;
}

export class TabNode extends BaseNode<Tab> {
    constructor(private readonly _renderer: Renderer) {
        super();
    }

    detectChanges(opts?: BubbleOpts): void {
        try {
            this.renderContext.detectChanges();
        } catch {}
    }

    destroy(): void {
        // nothing
    }
}

export interface TabEvent {
    event: 'click' | 'dblclick';
    tab: TabNode;
}

export interface DragDropEvent {
    event: 'dragstart' | 'drop' | 'dragenter' | 'dragleave' | 'dragend';
    tab: TabNode;
    index?: number;
}

interface BubbleOpts {
    emitEvent?: boolean;
    onlySelf?: boolean;
}

export class TabsetNode extends BaseNode<ExperimentalTabset> {
    children: TabNode[];
    eventsSub: Subject<TabEvent | DragDropEvent>;
    events: Observable<TabEvent | DragDropEvent>;
    selected: TabNode;
    keyboard: Keyboard;

    constructor(private readonly _renderer: Renderer) {
        super();
        this.children = [];
        this.eventsSub = new Subject();
        this.events = this.eventsSub.asObservable();
        this.keyboard = new Keyboard(this);
    }

    detectChanges(opts?: BubbleOpts): void {
        try {
            this.renderContext.detectChanges();
        } catch {}
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (opts && opts.onlySelf) return;
        this.children.forEach((n) => n.detectChanges());
    }

    destroy(): void {
        if (this.eventsSub) {
            this.eventsSub.complete();
        }
    }

    appendChildren(defs: Tab[]): void {
        defs.forEach((d) => {
            const n = this._renderer.tab(d);
            this.children.push(n);
        });
    }

    move(id: string, index: number): void {
        if (id == null || index >= this.children.length || index < 0) return;
        const moving = this.children.find((n) => n.def.id === id);
        if (moving.def.fixed) return;
        const before = this.children[index];
        if (before.def.fixed) return;
        const last = this.children[this.children.length - 1];
        const fromIndex = this.children.indexOf(moving);
        moveTab(last, moving, before, fromIndex, index, this);
    }

    select(id: string): void {
        let tab: TabNode;
        if (id != null) {
            tab = this.children.find((n) => n.def.id === id);
            if (!tab) return;
        }
        this.selected = tab;
        this.eventsSub.next({ event: 'click', tab: this.selected });
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function moveTab(
    last: TabNode,
    moving: TabNode,
    before: TabNode,
    fromIndex: number,
    toIndex: number,
    tabset: TabsetNode,
) {
    if (before === last) {
        before.renderContext.element.parentElement.appendChild(
            moving.renderContext.element,
        );
    } else {
        if (fromIndex > toIndex) {
            before.renderContext.element.parentElement.insertBefore(
                moving.renderContext.element,
                before.renderContext.element,
            );
        } else {
            before.renderContext.element.parentElement.insertBefore(
                moving.renderContext.element,
                before.renderContext.element.nextSibling,
            );
        }
    }
    tabset.children.splice(fromIndex, 1);
    tabset.children.splice(toIndex, 0, moving);
}

function bindClick(node: TabNode, tabset: TabsetNode): void {
    if (!node.renderContext) return;
    const el = node.renderContext.element;
    merge(fromEvent(el, 'click'), fromEvent(el, 'dblclick'))
        .pipe(
            tap((e) =>
                tabset.eventsSub.next({ event: e.type as any, tab: node }),
            ),
            catchError(() => of(null)),
        )
        .subscribe();
}

function bindDragDrop(node: TabNode, tabset: TabsetNode): void {
    if (!node.renderContext) return;
    const el = node.renderContext.element;
    el.draggable = true;
    // TODO unsub
    merge(
        fromEvent(el, 'dragstart'),
        fromEvent(el, 'dragover'),
        fromEvent(el, 'drop'),
        fromEvent(el, 'dragenter'),
        fromEvent(el, 'dragleave'),
        fromEvent(el, 'dragend'),
    )
        .pipe(
            tap((e: DragEvent) => {
                const target = (e.currentTarget || e.target) as HTMLElement;
                if (e.type === 'dragstart') {
                    const tab = tabset.children.find(
                        (n) => n.renderContext.element === target,
                    );
                    e.dataTransfer.setData('text', tab.def.id);
                    e.dataTransfer.effectAllowed = 'move';
                    tabset.eventsSub.next({ event: 'dragstart', tab });
                }
                if (e.type === 'dragend') {
                    const tab = tabset.children.find(
                        (n) => n.renderContext.element === target,
                    );
                    tabset.eventsSub.next({ event: 'dragend', tab });
                }
                if (e.type === 'dragenter') {
                    const tab = tabset.children.find(
                        (n) => n.renderContext.element === target,
                    );
                    tabset.eventsSub.next({ event: 'dragenter', tab });
                }
                if (e.type === 'dragleave') {
                    const tab = tabset.children.find(
                        (n) => n.renderContext.element === target,
                    );
                    tabset.eventsSub.next({ event: 'dragleave', tab });
                }
                if (e.type === 'dragover') {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                }
                if (e.type === 'drop') {
                    e.preventDefault();

                    // const id = e.dataTransfer.getData('id');
                    // const last = tabset.children[tabset.children.length - 1];
                    // const moving = tabset.children.find(n => n.def.id === id);
                    // const before = tabset.children.find(n => n.renderContext.element === target);
                    // const fromIndex = tabset.children.indexOf(moving);
                    // const toIndex = tabset.children.indexOf(before);
                    // moveTab(last, moving, before, fromIndex, toIndex, tabset);

                    const id = e.dataTransfer.getData('text');
                    const moving = tabset.children.find((n) => n.def.id === id);
                    const before = tabset.children.find(
                        (n) => n.renderContext.element === target,
                    );
                    const toIndex = tabset.children.indexOf(before);
                    tabset.move(id, toIndex);

                    tabset.eventsSub.next({
                        event: 'drop',
                        tab: moving,
                        index: toIndex,
                    });
                }
            }),
            catchError(() => of(null)),
        )
        .subscribe();
}

function bindKeyboard(node: TabNode, tabset: TabsetNode): void {
    if (!node.renderContext) return;
    const el = node.renderContext.element;
    fromEvent<KeyboardEvent>(el, 'keydown')
        .pipe(tap((e) => tabset.keyboard.handleEvent(e)))
        .subscribe();
}

function decorateTabset(node: TabsetNode): void {
    // nothing
}

function decorateTab(node: TabNode): void {
    const content = node.renderContext.element;
    const wrapper = document.createElement('div');
    wrapper.tabIndex = -1;
    wrapper.style.display = 'inline-flex';
    wrapper.appendChild(content);

    node.renderContext.element = wrapper;
    node.renderContext.appendElement = content;
}

function renderNode(node: BaseNode, renderFn: RenderFn): void {
    node.renderContext = renderFn(node.context);
}

function destroyNode(node: BaseNode): void {
    if (node.renderContext) {
        node.renderContext.element = null;
        node.renderContext.detectChanges = null;
        node.renderContext.destroy(node);
        node.renderContext.destroy = null;
    }
    node.renderContext = null;
}

export interface RendererOptions {
    orientation: 'horizontal' | 'vertical';
    tabs: Tab[];
    defaultRenderFn: RenderFn;
}

export class Renderer {
    constructor(public readonly opts?: RendererOptions) {}

    tabset(def: ExperimentalTabset): TabsetNode {
        const node = new TabsetNode(this);
        node.def = def;
        node.context = { type: 'tabset', ctx: def.ctx };
        node.appendChildren(this.opts.tabs);
        return node;
    }

    tab(def: Tab): TabNode {
        const node = new TabNode(this);
        node.def = def;
        node.context = { type: 'tab', tab: node, ctx: def.ctx };
        return node;
    }

    render(node: BaseNode): void {
        renderNode(node, node.def.render || this.opts.defaultRenderFn);
        if (node instanceof TabsetNode) {
            decorateTabset(node);
            node.children.forEach((n, i) => {
                this.render(n);
                decorateTab(n);
                bindClick(n, node);
                if (!n.def.fixed) {
                    bindDragDrop(n, node);
                }
                bindKeyboard(n, node);
                this.appendChild(node, n);
                if (i === 0) {
                    n.renderContext.element.tabIndex = 0;
                }
                n.renderContext.detectChanges();
            });
        }
    }

    destroy(node: BaseNode): void {
        destroyNode(node);
        node.destroy();
        if (node instanceof TabsetNode) {
            node.children.forEach((n) => {
                this.removeChild(n);
                this.destroy(n);
            });
        }
    }

    appendChild(parentNode: BaseNode, node: BaseNode): void {
        if (!parentNode.renderContext || !node.renderContext) return;
        (
            parentNode.renderContext.appendElement ||
            parentNode.renderContext.element
        ).appendChild(node.renderContext.element);
    }

    removeChild(node: BaseNode): void {
        if (!node.renderContext || !node.renderContext.element.parentElement)
            return;
        node.renderContext.element.parentElement.removeChild(
            node.renderContext.element,
        );
    }
}

function autoSizeTabset(node: TabsetNode): void {
    if (!node.renderContext) return;
    const lookup: { [key: string]: number } = {};
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const children = node.children.filter(
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        (e) => e.renderContext && e.renderContext.element,
    );
    const flexChildren = children.filter((e) => !e.def.fixed);
    const fixedChildren = children.filter((e) => e.def.fixed);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const calc = (p, c) => {
        c.renderContext.element.style.maxWidth = '';
        lookup[c.def.id] =
            c.renderContext.element.getBoundingClientRect().width;
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        return p + lookup[c.def.id];
    };
    node.renderContext.element.style.overflow = 'hidden'; // prevent intermediate scrollbars when auto-sizing
    const contentWidth = flexChildren.reduce(calc, 0);
    const fixedWidth = fixedChildren.reduce(calc, 0);
    const width =
        node.renderContext.element.getBoundingClientRect().width - fixedWidth;
    node.renderContext.element.style.overflow = '';
    if (width < contentWidth) {
        const maxWidth = width / flexChildren.length;
        const toResize = [];
        let distributiveWidth = width;
        flexChildren.forEach((e) => {
            if (lookup[e.def.id] < maxWidth) {
                distributiveWidth -= lookup[e.def.id];
            } else {
                toResize.push(e);
            }
        });
        let willResize = toResize.length;
        toResize.sort((a, b) => lookup[a.def.id] - lookup[b.def.id]);
        toResize.forEach((e) => {
            const next = distributiveWidth / willResize;
            if (lookup[e.def.id] < next) {
                distributiveWidth -= lookup[e.def.id];
                willResize--;
                return;
            }
            e.renderContext.element.style.maxWidth = `${next}px`;
        });
    }
}

class ResizeObserver {
    elSubs: Array<{ element: Element; sub: Subscription }> = [];

    constructor(public callback) {}

    bind(element): Observable<Event> {
        const expand = document.createElement('div');
        expand.style.position = 'absolute';
        expand.style.left = '0px';
        expand.style.top = '0px';
        expand.style.right = '0px';
        expand.style.bottom = '0px';
        expand.style.overflow = 'hidden';
        expand.style.visibility = 'hidden';
        expand.style.zIndex = '-1';

        const expandChild = document.createElement('div');
        expandChild.style.position = 'absolute';
        expandChild.style.left = '0px';
        expandChild.style.top = '0px';
        expandChild.style.width = '10000000px';
        expandChild.style.height = '10000000px';
        expand.appendChild(expandChild);

        const shrink = document.createElement('div');
        shrink.style.position = 'absolute';
        shrink.style.left = '0px';
        shrink.style.top = '0px';
        shrink.style.right = '0px';
        shrink.style.bottom = '0px';
        shrink.style.overflow = 'hidden';
        shrink.style.visibility = 'hidden';
        expand.style.zIndex = '-1';

        const shrinkChild = document.createElement('div');
        shrinkChild.style.position = 'absolute';
        shrinkChild.style.left = '0px';
        shrinkChild.style.top = '0px';
        shrinkChild.style.width = '200%';
        shrinkChild.style.height = '200%';
        shrink.appendChild(shrinkChild);

        element.appendChild(expand);
        element.appendChild(shrink);

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const setScroll = () => {
            expand.scrollLeft = expand.scrollWidth;
            expand.scrollTop = expand.scrollHeight;

            shrink.scrollLeft = shrink.scrollWidth;
            shrink.scrollTop = shrink.scrollHeight;
        };
        setScroll();

        let rect = element.getBoundingClientRect();
        let currentWidth = rect.width;
        let currentHeight = rect.height;
        return merge(
            fromEvent(expand, 'scroll'),
            fromEvent(shrink, 'scroll'),
        ).pipe(
            auditTime(0, animationFrameScheduler),
            tap(() => {
                rect = element.getBoundingClientRect();

                if (
                    currentWidth !== rect.width ||
                    currentHeight !== rect.height
                ) {
                    currentWidth = rect.width;
                    currentHeight = rect.height;

                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.callback && this.callback();
                }

                setScroll();
            }),
        );
    }

    observe(element: Element): void {
        if (
            this.elSubs &&
            this.elSubs.findIndex((e) => e.element === element) === -1
        ) {
            this.elSubs.push({ element, sub: this.bind(element).subscribe() });
        }
    }

    unobserve(element: Element): void {
        const index = this.elSubs.findIndex((e) => e.element === element);
        if (index !== -1) {
            const elSub = this.elSubs.splice(index, 0)[0];
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            elSub.sub && elSub.sub.unsubscribe();
        }
    }

    disconnect(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.elSubs && this.elSubs.forEach((e) => this.unobserve(e.element));
    }
}

export class Controller {
    private readonly _done: Subject<any>;
    renderer: Renderer;
    tabsetNode: TabsetNode;

    constructor() {
        this._done = new Subject();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    layout(): Promise<any> {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const resizeObserver = window['ResizeObserver'] || ResizeObserver;
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (resizeObserver === window['ResizeObserver']) {
            console.log('using native resize observer');
        }

        let ro;
        fromEventPattern(
            (handler) => {
                // eslint-disable-next-line new-cap
                ro = new resizeObserver(handler);
                ro.observe(this.tabsetNode.renderContext.element);
            },
            () => {
                ro.disconnect();
            },
        )
            .pipe(
                takeUntil(this._done),
                auditTime(0, animationFrameScheduler),
                tap(() => autoSizeTabset(this.tabsetNode)),
            )
            .subscribe();

        return Promise.resolve();
    }

    destroy(): void {
        this._done.next();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    autoSizeTabset(): Promise<any> {
        return scheduled(of(null), animationFrameScheduler)
            .pipe(tap(() => autoSizeTabset(this.tabsetNode)))
            .toPromise();
    }
}

export const KeyCodeKeyMap = {
    9: 'Tab',
    13: 'Enter',
    27: 'Escape',
    32: 'Space',
    37: 'ArrowLeft',
    38: 'ArrowUp',
    39: 'ArrowRight',
    40: 'ArrowDown',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const KeyKeyCodeMap = {
    Tab: 9,
    Enter: 13,
    Escape: 27,
    Space: 32,
    ArrowLeft: 37,
    ArrowUp: 38,
    ArrowRight: 39,
    ArrowDown: 40,
};

const Key = {
    Tab: 'Tab',
    Enter: 'Enter',
    Escape: 'Escape',
    Space: ' ',
    ArrowLeft: 'ArrowLeft',
    ArrowUp: 'ArrowUp',
    ArrowRight: 'ArrowRight',
    ArrowDown: 'ArrowDown',
};

enum ResultAction {
    IGNORE,
    NO_PROPAGATE,
    NO_DEFAULT,
    BOTH,
}

function focus(current: HTMLElement | HTMLElement[], next: HTMLElement): void {
    if (!next) return;
    if (current) {
        // eslint-disable-next-line no-return-assign
        (!Array.isArray(current) ? [current] : current).forEach(
            (value) => (value.tabIndex = -1),
        );
    }
    next.tabIndex = 0;
    next.focus();
}

// https://dev.opera.com/articles/accessible-drag-and-drop/
class Keyboard {
    rearranging: boolean;
    private _src: TabNode;
    private _lastIndex: number;

    constructor(private readonly _tabset: TabsetNode) {
        this._lastIndex = -1;
    }

    private _handleEvent($event: KeyboardEvent): ResultAction {
        const target = $event.target as HTMLElement;
        if (!target) return ResultAction.IGNORE;
        const key = $event.key || KeyCodeKeyMap[$event.keyCode];

        if (key === Key.Space) {
            if (!this.rearranging) {
                const tab = this._tabset.children.find(
                    (n) => n.renderContext.element === target,
                );
                if (!tab.def.fixed) {
                    this.rearranging = true;
                    this._src = tab;
                    this._tabset.eventsSub.next({
                        event: 'dragstart',
                        tab: this._src,
                    });
                }
            }
        }

        if (key === Key.Enter) {
            if (this.rearranging) {
                const before = this._tabset.children[this._lastIndex];
                this._tabset.move(this._src.def.id, this._lastIndex);
                focus(
                    this._tabset.children.map((n) => n.renderContext.element),
                    this._src.renderContext.element,
                );
                this._lastIndex = -1;
                this._src = null;
                this.rearranging = false;
                this._tabset.eventsSub.next({
                    event: 'drop',
                    tab: this._src,
                    index: this._lastIndex,
                });
                this._tabset.eventsSub.next({
                    event: 'dragleave',
                    tab: before,
                });
                this._tabset.eventsSub.next({
                    event: 'dragend',
                    tab: this._src,
                });
            }
        }

        if (key === Key.Escape) {
            if (this.rearranging) {
                this._lastIndex = -1;
                this._src = null;
                this.rearranging = false;
                this._tabset.eventsSub.next({
                    event: 'dragend',
                    tab: this._src,
                });
            }
        }

        if (key === Key.ArrowLeft || key === Key.ArrowRight) {
            const tabs = this._tabset.children;
            const index =
                !this.rearranging || this._lastIndex === -1
                    ? tabs.findIndex((n) => n.renderContext.element === target)
                    : this._lastIndex;
            const current = tabs[index];

            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const nextIndex = (index: number) => {
                const goToPrevious = key === Key.ArrowLeft;
                const nextIndex = index + (goToPrevious ? -1 : 1);
                return nextIndex >= tabs.length
                    ? 0
                    : nextIndex < 0
                    ? tabs.length - 1
                    : nextIndex;
            };

            let finalIndex = nextIndex(index);
            let next = tabs[finalIndex];
            if (this.rearranging) {
                while (next.def.fixed) {
                    finalIndex = nextIndex(finalIndex);
                    next = tabs[finalIndex];
                    if (index === finalIndex) break;
                }
            }

            if (!this.rearranging) {
                focus(
                    tabs.map((n) => n.renderContext.element),
                    next.renderContext.element,
                );
                next.renderContext.element.focus();
            } else {
                this._lastIndex = finalIndex;
                this._tabset.eventsSub.next({
                    event: 'dragleave',
                    tab: current,
                });
                this._tabset.eventsSub.next({ event: 'dragenter', tab: next });
            }
        }
    }

    handleEvent($event: KeyboardEvent): void {
        const ret = this._handleEvent($event);
        switch (ret) {
            case ResultAction.NO_PROPAGATE:
                $event.stopPropagation();
                break;
            case ResultAction.NO_DEFAULT:
                $event.preventDefault();
                break;
            case ResultAction.BOTH:
                $event.stopPropagation();
                $event.preventDefault();
        }
    }
}
